import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAdd, faClose } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import React, { useMemo } from "react";
import { Button } from "./button";
import { Combobox } from "./combobox";

export type ChipComboboxOption = {
  renderChip: () => React.ReactNode;
  renderOption: () => React.ReactNode;
  value: string;
  searchableText: string;
};

export type ChipComboboxProps = {
  groups: {
    groupName: string;
    options: ChipComboboxOption[];
  }[];
  selected: string[];
  onSelectionChange: (value: string, selected: boolean) => void;
  disabled?: boolean;
};

export function ChipCombobox({
  groups,
  selected,
  onSelectionChange,
  disabled = false,
}: ChipComboboxProps) {
  const selectedSet = new Set(selected);
  const selectedOptionsByGroup = useMemo(() => {
    const alreadySelected = new Set();
    const selectedOptionsByGroup: Record<string, ChipComboboxOption[]> = {};
    for (const group of groups) {
      for (const option of group.options) {
        if (
          selectedSet.has(option.value) &&
          !alreadySelected.has(option.value)
        ) {
          if (!(group.groupName in selectedOptionsByGroup)) {
            selectedOptionsByGroup[group.groupName] = [];
          }
          selectedOptionsByGroup[group.groupName].push(option);
          alreadySelected.add(option.value);
        }
      }
    }
    return selectedOptionsByGroup;
  }, [groups]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-0.5">
        {Object.values(selectedOptionsByGroup).map((options, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className="bg-muted-foreground mx-1 h-4 w-[1px]"></span>
            )}
            {options.map((option) => (
              <Button
                key={option.value}
                onClick={() => onSelectionChange(option.value, false)}
                size="sm"
                variant="outline"
                className={cn("flex items-center gap-2")}
                disabled={disabled}
              >
                {option.renderChip()}
                <FontAwesomeIcon icon={faClose} />
              </Button>
            ))}
          </React.Fragment>
        ))}
        <Combobox
          groups={groups.map((group) => ({
            groupName: group.groupName,
            options: group.options.map((option) => ({
              render: option.renderOption,
              searchableText: option.searchableText,
              value: option.value,
              renderWhenSelected: option.renderChip,
            })),
          }))}
          selected={selected}
          onSelectionChange={onSelectionChange}
          disabled={disabled}
        >
          <Button variant="outline" size="sm" disabled={disabled}>
            <FontAwesomeIcon icon={faAdd} />
          </Button>
        </Combobox>
      </div>
    </div>
  );
}
